<template>
  <div class="sidebar" :class="{ 'sidebar-open': isOpen }">
    <div class="sidebar-header">
      <img src="../../assets/dcx.png" alt="Logo" class="logo" />
      <h3><ShinyText text="Quản Trị" :speed="4" /></h3>
    </div>

    <div class="user-info">
      <div class="avatar">{{ userInitials }}</div>
      <div class="user-details">
        <p class="user-name">{{ dynamicFullName }}</p>
        <p class="user-role">{{ getRoleLabel }}</p>
      </div>
    </div>

    <nav class="sidebar-nav">
      <router-link v-if="isAdmin || isAccountant" to="/dashboard" class="nav-item">
        <i class="fas fa-chart-line"></i>
        <span>Thống Kê</span>
      </router-link>
      <router-link to="/bookings" class="nav-item">
        <i class="fas fa-ticket-alt"></i>
        <span>Đặt Vé</span>
      </router-link>
      <router-link to="/routes" class="nav-item">
        <i class="fas fa-route"></i>
        <span>Tuyến Đường</span>
      </router-link>
      <router-link to="/vehicles" class="nav-item">
        <i class="fas fa-bus"></i>
        <span>Phương Tiện</span>
      </router-link>
      <router-link to="/trips" class="nav-item">
        <i class="fas fa-map-marked-alt"></i>
        <span>Chuyến Đi</span>
      </router-link>
      <router-link to="/orders" class="nav-item">
        <i class="fas fa-shopping-cart"></i>
        <span>Đơn Hàng</span>
      </router-link>
      <router-link to="/admin/discount-codes" class="nav-item" active-class="active">
        <i class="fas fa-tags"></i>
        <span>Mã giảm giá</span>
      </router-link>
      <router-link to="/admin/addon-products" class="nav-item">
        <i class="fas fa-box"></i>
        <span>Dịch vụ bổ sung</span>
      </router-link>
      <router-link to="/admin/blocked-phones" class="nav-item">
        <i class="fas fa-phone-slash"></i>
        <span>SĐT bị chặn</span>
      </router-link>
      <router-link to="/admin/group-bookings" class="nav-item">
        <i class="fas fa-users"></i>
        <span>Đặt nhóm</span>
      </router-link>
      <router-link v-if="isAdmin" to="/admin/batch-email" class="nav-item">
        <i class="fas fa-envelope"></i>
        <span>Gửi Email</span>
      </router-link>
      <router-link v-if="isAdmin || isAccountant" to="/invoices" class="nav-item">
        <i class="fas fa-file-invoice"></i>
        <span>Hóa đơn điện tử</span>
      </router-link>
      <router-link v-if="isAdmin" to="/invoice-config" class="nav-item">
        <i class="fas fa-cog"></i>
        <span>Cấu hình SePay</span>
      </router-link>
      <router-link v-if="isAdmin" to="/admin/users" class="nav-item">
        <i class="fas fa-user-cog"></i>
        <span>Quản Lý Nhân Viên</span>
      </router-link>

      <!-- Logout button in sidebar -->
      <div class="nav-divider"></div>
      <button @click="handleLogout" class="nav-item logout-sidebar-btn">
        <i class="fas fa-sign-out-alt"></i>
        <span>Đăng Xuất</span>
      </button>
    </nav>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import ShinyText from '@/components/ui/ShinyText.vue'
import axiosInstance from '@/plugins/axios'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: true
  },
  userData: {
    type: Object,
    default: () => ({
      full_name: 'Admin User',
      role: 'Administrator'
    })
  }
})

const router = useRouter()

const isAdmin = computed(() => {
  try {
    const token = localStorage.getItem('adminToken')
    if (!token) return false
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.role === 'admin'
  } catch (e) {
    return false
  }
})

const isAccountant = computed(() => {
  try {
    const token = localStorage.getItem('adminToken')
    if (!token) return false
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.role === 'accountant'
  } catch (e) {
    return false
  }
})

const getRoleLabel = computed(() => {
  try {
    const token = localStorage.getItem('adminToken')
    if (!token) return props.userData.role
    const payload = JSON.parse(atob(token.split('.')[1]))
    const role = payload.role
    if (role === 'admin') return 'Admin'
    if (role === 'accountant') return 'Kế Toán'
    return 'Staff'
  } catch (e) {
    return props.userData.role
  }
})

const userInitials = computed(() => {
  return dynamicFullName.value
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .substring(0, 2)
})

const dynamicFullName = computed(() => {
  try {
    const token = localStorage.getItem('adminToken')
    if (!token) return props.userData.full_name
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const pad = base64.length % 4
    const padded = pad ? base64 + '='.repeat(4 - pad) : base64
    const jsonPayload = decodeURIComponent(atob(padded).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    }).join(''))
    const payload = JSON.parse(jsonPayload)
    return payload.full_name || payload.sub || props.userData.full_name
  } catch (e) {
    return props.userData.full_name
  }
})

const handleLogout = () => {
  // Revoke the server-side session first (bumps password_version so every
  // issued token dies), then clear local storage. Fire-and-forget so a
  // network failure never blocks local logout.
  const token = localStorage.getItem('adminToken')
  if (token) {
    axiosInstance.post('/admin/logout', {}, {
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {})
  }
  localStorage.removeItem('adminToken')
  localStorage.removeItem('refreshToken')
  router.push('/login')
}
</script>

<style scoped>
/* Modern Sidebar Styling - Forest Green Theme */
.sidebar {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: 260px;
  background: linear-gradient(180deg, #283e37 0%, #1f312c 100%);
  color: white;
  z-index: 1000;
  transform: translateX(-100%);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
}

.sidebar-open {
  transform: translateX(0);
}

.sidebar-header {
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: 1rem;
  background: rgba(0, 0, 0, 0.1);
}

.sidebar-header h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  letter-spacing: -0.02em;
}

.logo {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  object-fit: contain;
  background: white;
  padding: 4px;
}

.user-info {
  padding: 1.25rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.875rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.05);
}

.avatar {
  width: 42px;
  height: 42px;
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 0.025em;
  box-shadow: 0 2px 8px rgba(72, 187, 120, 0.3);
}

.user-details {
  flex: 1;
  min-width: 0;
}

.user-details .user-name {
  margin: 0;
  font-weight: 600;
  font-size: 0.95rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-details .user-role {
  margin: 0.25rem 0 0 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: capitalize;
}

.sidebar-nav {
  padding: 1rem 0;
  flex: 1;
  overflow-y: auto;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.875rem 1.5rem;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  transition: all 0.2s ease;
  font-size: 0.925rem;
  font-weight: 500;
  border-left: 3px solid transparent;
  margin: 2px 0;
}

.nav-item i {
  width: 20px;
  text-align: center;
  font-size: 1rem;
  opacity: 0.85;
}

.nav-item span {
  flex: 1;
}

.nav-item:hover {
  background: rgba(255, 255, 255, 0.08);
  color: white;
  border-left-color: rgba(72, 187, 120, 0.5);
}

.nav-item:hover i {
  opacity: 1;
}

.nav-item.router-link-active,
.nav-item.active {
  background: rgba(72, 187, 120, 0.15);
  color: white;
  border-left-color: #48bb78;
}

.nav-item.router-link-active i,
.nav-item.active i {
  color: #48bb78;
  opacity: 1;
}

.nav-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  margin: 0.75rem 1.5rem;
}

.logout-sidebar-btn {
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.925rem;
}

.logout-sidebar-btn:hover {
  background: rgba(229, 62, 62, 0.15);
  color: #fc8181;
  border-left-color: #e53e3e;
}

.logout-sidebar-btn:hover i {
  color: #fc8181;
}

/* Scrollbar styling */
.sidebar-nav::-webkit-scrollbar {
  width: 4px;
}

.sidebar-nav::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-nav::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
}

.sidebar-nav::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Large screen: no change needed, sidebar controlled by isOpen prop */

@media (max-width: 480px) {
  .sidebar {
    width: 280px;
  }
  
  .nav-item {
    padding: 1rem 1.5rem;
  }
}
</style>
